// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { Box } from '@mui/material';
import { SyncButton } from '../Button/SyncButton';
import { FormControlLabel } from './FormControlLabel';
import TextInput from './TextInput';
import { PropertyEntry, getPropertyErrors } from './customPropertiesValidation';

interface CustomPropertiesInputProps {
  properties: PropertyEntry[];
  setProperties: (value: PropertyEntry[]) => void;
  errors?: string[];
}

const CustomPropertiesInput = ({
  properties,
  setProperties,
  errors: externalErrors,
}: CustomPropertiesInputProps) => {
  const errors = externalErrors ?? getPropertyErrors(properties);

  // Curried for easier use in handlers
  const modifyPropertyKey = (property: PropertyEntry) => (key: string) => {
    setProperties(properties.map((p) => (p.id === property.id ? { ...p, key: key.trim() } : p)));
  };

  const modifyPropertyValue = (property: PropertyEntry) => (value: string) => {
    setProperties(properties.map((p) => (p.id === property.id ? { ...p, value } : p)));
  };

  const removeProperty = (property: PropertyEntry) => () => {
    setProperties(properties.filter((p) => p.id !== property.id));
  };

  return (
    <>
      <FormControlLabel label="Custom Properties" />
      {properties.map((property, index) => (
        <Box key={property.id} margin={2}>
          <TextInput
            enableTopLabel
            label={`Property Key ${index + 1}`}
            value={property.key}
            setValue={modifyPropertyKey(property)}
            validationMessage={errors[index]}
          />
          <TextInput
            enableTopLabel
            label={`Property Value ${index + 1}`}
            value={property.value}
            setValue={modifyPropertyValue(property)}
          />
          <SyncButton onClick={removeProperty(property)} sx={{ textTransform: 'none' }}>
            Remove Property
          </SyncButton>
        </Box>
      ))}
      <Box margin={2}>
        <SyncButton
          onClick={() => {
            setProperties([...properties, { id: crypto.randomUUID(), key: '', value: '' }]);
          }}
          sx={{ textTransform: 'none' }}
        >
          Add Property
        </SyncButton>
      </Box>
    </>
  );
};

export default CustomPropertiesInput;
